import { useEffect, useRef, useCallback } from "react";
import { useFormContext, useWatch } from "react-hook-form";

function makeOnChangeSupportNoneSyntheticEvents(onChange) {
    return (x) => {
        if (x.target === undefined) {
            x.target = x;
        }
        return onChange(x);
    };
}

/**
 * Returns a value and a change handler for optionally controlled components.
 * If used in a form context, the value will be controlled by the form, otherwise the value must be controlled by the parent, via value and onChange props.
 *
 * @param {string} name // name of the input for forms, must match the name used with the input in the consuming component, only used if not in form context
 * @param controlledValue // value prop, used as initial value if in uncontrolled mode
 * @param onChange // called when value changes, only used if not in form context
 * @param formOptions // options to pass to useFormContext
 * @param filterFunc // takes the value and returns the filtered value
 * @param {boolean} isReactDatePicker react-datepicker needs to be handled differently. Specifically, it needs to be in
 * controlled mode when used in react-hook-form (normally, react-hook-form uses uncontrolled mode). Also, when calling onChange,
 * it needs to be passed the event object instead of the value.
 * @returns {[propsForInput, setValue]}
 *  propsForInput is the props to pass to the input (name + value and onChange if controlled))
 *  setValue is a function that takes the new value and updates the value, useful if the input component needs to set the value without worrying if controlled/uncontrolled
 */
const useOptionallyControlled = (
    name,
    controlledValue,
    onChange,
    formOptions = {},
    filterFunc,
    isReactDatePicker = false,
) => {
    const formContext = useFormContext();
    const isControlled = isReactDatePicker || !formContext || (name === undefined && controlledValue !== undefined); //allow controlled even in form context if name is undefined AND controlledValue is defined and force controlled mode for react-datepicker

    useEffect(() => {
        if (!isControlled && controlledValue !== undefined) {
            if (formContext?.getValues(name) !== controlledValue) {
                console.debug(
                    "value prop set while used in form, using as initial value instead.",
                    "\n name:",
                    name,
                    "\n value:",
                    controlledValue,
                );
                formContext?.setValue(name, controlledValue);
            }
        }
    }, [isControlled, controlledValue, formContext, name]);

    if (!isControlled && name === undefined) {
        console.error(
            "this input is in a form but doesn't have a name prop! you're going to get an error form react hook form, probably 'TypeError: field is undefined'.",
        );
    }

    const useValue = () => {
        //need to always call useWatch because hooks must not be called conditionally, thats why we need to use a try catch, cause it will fail if not in form context
        try {
            const watched = useWatch({
                control: formContext.control,
                name: name,
                disabled: isControlled,
                defaultValue: "",
            });
            return isControlled ? controlledValue : watched;
        } catch (e) {
            if (isControlled) {
                // expected, since we aren't using a form
                return controlledValue;
            } else {
                // something else went wrong
                throw e;
            }
        }
    };

    const value = useValue();

    const onChangeRef = useRef(onChange);
    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);
    useEffect(() => {
        if (isControlled) {
            //if controlled, formatting and on change is already handled by the setter
            return;
        }

        const filtered = filterFunc ? filterFunc(value) : value;
        if (filtered !== value) {
            //filtering changed the value, so update the form, next time will handle further
            formContext.setValue(name, filtered, { shouldDirty: true });
        } else {
            //no filtering needed, so just call the onChange, as this is the last step
            onChangeRef.current?.(value);
        }
    }, [isControlled, value, filterFunc, formContext, name]);

    // sync autofill on first render with the form state (chrome needed this but not firefox??)
    {
        // refs to prevent re-running the useEffect
        const valueRef = useRef(value);
        useEffect(() => {
            valueRef.current = value;
        }, [value]);
        const setValueInternalRef = useRef(formContext?.setValue);
        useEffect(() => {
            setValueInternalRef.current = formContext?.setValue;
        }, [formContext?.setValue]);
        const nameRef = useRef(name);
        useEffect(() => {
            nameRef.current = name;
        }, [name]);

        // only runs post render + if controlled status changes (not actively supported)
        useEffect(() => {
            if (isControlled) {
                // only needed in controlled mode
                return;
            }
            const valueFromDom = document.querySelector(`input[name='${nameRef.current}']`)?.value;
            if (valueFromDom && valueFromDom !== valueRef.current) {
                setValueInternalRef.current(nameRef.current, valueFromDom, { shouldDirty: true });
            }
        }, [valueRef, setValueInternalRef, nameRef, isControlled]);
    }

    const setValue = useCallback(
        (newValue) => {
            const filtered = filterFunc ? filterFunc(newValue) : newValue;
            if (isControlled) {
                // if not using form, parent is responsible for updating value
                if (onChange) onChange(filtered);
            } else {
                // if using form, form-value change will trigger onChange in the useEffect above
                // this allows the parent to react normally even if the value is changed externally
                formContext.setValue(name, filtered, { shouldDirty: true });
            }
        },
        [isControlled, onChange, filterFunc, formContext, name],
    );

    const setValueFromEvent = (event) => {
        // If its a synthetic event, get the value from the target, some components just pass the value directly as a property of the event (react-select) so just take that if target isn't there.
        // react-datepicker needs the entire event object, you won't be able to edit the date using the keyboard otherwise (specifically, the entire value will be cleared when you start typing).
        setValue(isReactDatePicker ? event : event.target ? event.target.value : event.value);
    };

    const propsForInput = {
        name: name,
        ...(isControlled
            ? {
                  value: value,
                  onChange: setValueFromEvent,
              }
            : {
                  ...((props) => {
                      return {
                          ...props,
                          defaultValue: controlledValue, //some components won't see the value defaulted with setValue above, so we need this
                          //need this for react-select
                          onChange: makeOnChangeSupportNoneSyntheticEvents(props.onChange),
                      };
                  })(formContext?.register(name, formOptions)),
              }),
    };

    return [propsForInput, setValue, useValue];
};

export default useOptionallyControlled;
